import JSCookie from 'js-cookie';
import type { NextComponentType } from 'next';
import type { AppInitialProps, AppProps } from 'next/app';
import getConfig from 'next/config';
import React, { useEffect } from 'react';

import type { ThemeNames } from '@sravni/design-system-theme';
import { ThemeName } from '@sravni/design-system-theme';
import type { DeviceInfo } from '@sravni/koa-utils/lib/middlewares/device';
import type { ICommonHeadProps } from '@sravni/next-common-head';
import { CommonHead } from '@sravni/next-common-head';
import { BANNER_TYPES } from '@sravni/react-advertisement/lib/constants';
import { CookiesPolicyAgreementCard } from '@sravni/react-cookies-policy';
import { Notifications } from '@sravni/react-design-system/lib/Notifications';
import { Footer } from '@sravni/react-footer';
import { Interceptor } from '@sravni/react-ugc/lib/SentryInterceptor';
import { DeviceInfoProvider, ThemeProvider } from '@sravni/react-utils';
import { formatUserData } from '@sravni/utils/lib/auth';

import Header from 'src/components/Header';

import FingerprintJS from '@fingerprintjs/fingerprintjs';

import { ReduxBanner } from '../components/ReduxBanner';
import Seo from '../components/Seo';
import { prepareMetadataState } from '../helpers/seo';
import { useIsWlPage } from '../hooks/useIsWlPage';
import { StoreProvider } from '../providers/store';
import type { GlobalState } from '../store';
import { getOrCreateStore } from '../store';
import { setCurrentUserLocation } from '../store/reducers/locations';
import { setMetadata } from '../store/reducers/metadata';
import { setServices } from '../store/reducers/services';
import { setSettings } from '../store/reducers/sitesettings';
import { setFingerprint, setUser } from '../store/reducers/user';
import { getSiteSettings } from '../store/selectors/sitesettings';
import { getFingerprint, getUser } from '../store/selectors/users';
import styles from '../styles/page.module.scss';
import { sendSpaIsReadyEvent } from '../utils/analytics';
import { isProdBuild, isProduction } from '../utils/environment';

// eslint-disable-next-line import/no-unassigned-import
import '@sravni/design-system-theme/lib/globalStyles.css';
// eslint-disable-next-line import/no-unassigned-import
import 'intersection-observer';

interface IProps extends AppProps {
  realPath: string;
  initialReduxState: GlobalState;
  deviceInfo: DeviceInfo;
  theme: ThemeNames;
}

export const AB_NAME = 'AB_RATINGS';

const MyApp: NextComponentType<App.ExtendedContext, AppInitialProps, IProps> = (props) => {
  const { Component, pageProps, initialReduxState, deviceInfo, theme } = props;
  const reduxStore = getOrCreateStore(initialReduxState);
  const { getState, dispatch } = reduxStore;
  const user = getUser(getState());
  const sitesettings = getSiteSettings(getState());
  const { publicRuntimeConfig } = getConfig();
  const fingerprint = getFingerprint(getState());
  const isWl = useIsWlPage();

  const analyticsOptions: ICommonHeadProps['analyticsOptions'] = {
    enabled: isProduction,
    userId: user?.sub,
    googleTmId: 'GTM-PMDFG9',
    theme,
  };

  useEffect(() => {
    if (fingerprint === '-1') {
      FingerprintJS.load()
        .then((fp) => fp.get())
        .then((result) => {
          const id = result.visitorId;

          dispatch(setFingerprint(id));
          JSCookie.set('_FP_', id);
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { default: queue } = require('../utils/queue');

    if (isProdBuild) {
      queue.push(() => {
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.register('/service-worker.js').catch((err) => {
            console.warn('service worker registration failed', err.message);
          });
        }
      });
    }

    queue.push(() => sendSpaIsReadyEvent(deviceInfo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Interceptor componentName="rating-root-component" isProduction={isProduction}>
      <StoreProvider store={reduxStore}>
        <ThemeProvider initialTheme={theme}>
          <DeviceInfoProvider initialInfo={deviceInfo}>
            <>
              <CommonHead analyticsOptions={analyticsOptions} cdnUrl={publicRuntimeConfig.cdnUrl} />
              <Seo />
              <div className={styles.wrapper}>
                {!isWl && (
                  <>
                    <ReduxBanner type={BANNER_TYPES.TOP} />
                    <Header />
                  </>
                )}
                <Component {...pageProps} />
              </div>
              {!isWl && (
                <Footer
                  menuLinks={sitesettings.footer?.menu}
                  socialLinks={sitesettings.footer?.socialLinks}
                  support={sitesettings.footer?.support}
                  className="h-mt-40"
                />
              )}
              <Notifications />
              <CookiesPolicyAgreementCard companyName="sravni" />
            </>
          </DeviceInfoProvider>
        </ThemeProvider>
      </StoreProvider>
    </Interceptor>
  );
};

const ctxFieldsToPutToState = [
  {
    field: '__USER__',
    action: setUser,
    prepare: (user, _ctx) => formatUserData(user),
  },
  {
    field: '__SITE_SETTINGS__',
    action: setSettings,
  },
  {
    field: '__USER_LOCATION__',
    action: setCurrentUserLocation,
  },
  {
    field: '__SERVICES__',
    action: setServices,
  },
  {
    field: '__SEO__',
    action: setMetadata,
    prepare: (seo, ctx) => prepareMetadataState({ seo, pathname: ctx.asPath.split('?') }),
  },
];

MyApp.getInitialProps = async ({ Component, ctx }: App.ReduxAppContext) => {
  let pageProps = {};
  const reduxStore = getOrCreateStore();
  ctx.store = reduxStore;

  if (typeof window === 'undefined') {
    ctxFieldsToPutToState.forEach(({ field, action, prepare }) => {
      const data = ctx.req[field];
      if (data) {
        reduxStore.dispatch(action(prepare ? prepare(data, ctx) : data));
      }
    });
  }

  if (Component.getInitialProps) {
    pageProps = await Component.getInitialProps(ctx);
  }

  let deviceInfo: DeviceInfo = { phone: false, tablet: false, isAppleMobile: false };
  let theme: ThemeNames = ThemeName.lager;

  if (ctx.req?.__DEVICE_INFO__) {
    deviceInfo = ctx.req.__DEVICE_INFO__;
  }
  if (ctx.req?.__THEME__) {
    theme = ctx.req.__THEME__;
  }

  return {
    pageProps,
    initialReduxState: reduxStore.getState(),
    realPath: ctx.req?.url || '',
    deviceInfo,
    theme,
  };
};

export default MyApp;
